import React, { Suspense, lazy, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleAnalytics } from './components/Analytics';
import { Loadable } from './components/common/Loadable';
import ErrorBoundary from './components/common/ErrorBoundary';
import { UserProvider, useUser } from './contexts/UserContext';
import { UserTierProvider } from './contexts/UserTierContext';
import { AddOnProvider } from './contexts/AddOnContext';
import { AuthProvider } from './contexts/AuthContext';
import { AIDrawerProvider } from './contexts/AIDrawerContext';
import { UserSkillProvider } from './contexts/UserSkillContext';
import DashboardRedirect from './components/routing/DashboardRedirect';
import { ToastContainer } from './components/ui/Toast';
import FloatingTeamChat from './components/chat/FloatingTeamChat';

// Performance monitoring and error tracking
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { trackWebVitals, preloadCriticalResources } from './utils/performance';

// Initialize Sentry for error tracking
const initializeSentry = () => {
  // Skip Sentry initialization in development or if DSN is not set
  if (process.env.NODE_ENV !== 'production' || !process.env.REACT_APP_SENTRY_DSN) {
    console.log('Sentry initialization skipped - not in production or DSN not set');
    return;
  }

  try {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      release: process.env.REACT_APP_VERSION || `v${Date.now()}`,
      environment: process.env.NODE_ENV || 'production',
      debug: process.env.NODE_ENV !== 'production',
      integrations: [new Integrations.BrowserTracing()],
      tracesSampleRate: 0.2,
      // Disable automatic session tracking for now
      autoSessionTracking: false,
      // Disable automatic performance monitoring for now
      _experiments: {
        // Enable metrics in the Errors inbox
        metricsAggregator: true,
      },
    });
    
    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
};

// Initialize Sentry when the app loads
initializeSentry();

// Track web vitals in development and production
try {
  trackWebVitals();
} catch (error) {
  console.error('Failed to initialize web vitals tracking:', error);
}

// Preload function for route components
const preloadComponent = (importFunc) => {
  const component = importFunc();
  if (typeof window !== 'undefined') {
    component.preload?.();
  }
  return component;
};

// Lazy load components for better performance with preloading
const TierRouter = Loadable(() => import('./components/routing/TierRouter'));

// Public pages
const HomePage = Loadable(() => import('./pages/HomePage'));
const FeaturesPage = Loadable(() => import('./pages/FeaturesPage'));
const PricingPage = Loadable(() => import('./pages/PricingPage'));
const AddOnsPage = Loadable(() => import('./pages/AddOnsPage'));
const EnterprisePricingPage = Loadable(() => import('./pages/EnterprisePricingPage'));
const EnterpriseContactPage = Loadable(() => import('./pages/EnterpriseContactPage'));
const AboutPage = Loadable(() => import('./pages/AboutPage'));
const ContactPage = Loadable(() => import('./pages/ContactPage'));
const AIAgentsPage = Loadable(() => import('./pages/AIAgentsPage'));
const BlogPage = Loadable(() => import('./pages/BlogPage'));
const BlogPost = Loadable(() => import('./pages/BlogPost'));
const PlatformDemo = Loadable(() => import('./pages/resources/PlatformDemo'));
const DemoWorkspacePage = Loadable(() => import('./pages/DemoWorkspacePage'));
const FullFeaturedExample = Loadable(() => import('./pages/examples/FullFeaturedExample'));

// Resource pages
const AIMarketingPlaybook = Loadable(() => import('./pages/resources/AIMarketingPlaybook'));
const ContentTemplates = Loadable(() => import('./pages/resources/ContentTemplates'));
const IntegrationsPage = Loadable(() => import('./pages/IntegrationsPage'));
const DocumentationPage = Loadable(() => import('./pages/DocumentationPage'));
const CareersPage = Loadable(() => import('./pages/CareersPage'));
const ResourcesPage = Loadable(() => import('./pages/ResourcesPage'));

// Auth pages
const LoginPage = Loadable(() => import('./pages/LoginPage'));
const SignupPage = Loadable(() => import('./pages/SignupPage'));
const ForgotPasswordPage = Loadable(() => import('./pages/ForgotPasswordPage'));
const ContactSalesPage = Loadable(() => import('./pages/ContactSalesPage'));

// Payment pages
const CheckoutPage = Loadable(() => import('./pages/CheckoutPage'));

// Client pages
const AIAssistantPage = Loadable(() => import('./pages/client/AIAssistantPage'));
const AIDocumentationPage = Loadable(() => import('./pages/client/AIDocumentationPage'));
const CampaignManagerPage = Loadable(() => import('./pages/client/CampaignManagerPage'));
const WorkflowsPage = Loadable(() => import('./pages/client/WorkflowsPage'));
const ReportsPage = Loadable(() => import('./pages/client/ReportsPage'));
const AnalyticsPage = Loadable(() => import('./pages/client/AnalyticsPage'));
const SettingsPage = Loadable(() => import('./pages/client/SettingsPage'));
const BrandVoiceManagerPage = Loadable(() => import('./pages/client/BrandVoiceManagerPage'));
const AdOpsHubPage = Loadable(() => import('./pages/client/AdOpsHubPage'));
const EnterpriseReportsPage = Loadable(() => import('./pages/client/EnterpriseReportsPage'));
const SecurityCompliancePage = Loadable(() => import('./pages/client/SecurityCompliancePage'));

// Admin Layout and Auth
const AdminLayout = Loadable(() => import('./components/admin/AdminLayout'));
const AdminLogin = Loadable(() => import('./pages/admin/AdminLogin'));
const ProtectedRoute = Loadable(() => import('./components/common/ProtectedRoute'));

// Admin pages with preloading
const adminPages = {
  SimplifiedDashboard: Loadable(() => import('./pages/admin/SimplifiedDashboard')),
  AdminDashboardPro: Loadable(() => import('./pages/admin/AdminDashboardPro')),
  PageManagement: Loadable(() => import('./pages/admin/PageManagement')),
  PageEditor: Loadable(() => import('./components/admin/page-builder/PageEditor')),
  ClientPortalManagement: Loadable(() => import('./pages/admin/ClientPortalManagement')),
  ContentManagement: Loadable(() => import('./pages/admin/ContentManagement')),
  UserManagement: Loadable(() => import('./pages/admin/UserManagement')),
  ActivityLog: Loadable(() => import('./pages/admin/ActivityLog')),
  UserRolesPage: Loadable(() => import('./pages/admin/UserRolesPage')),
  SecurityCenter: Loadable(() => import('./pages/admin/SecurityCenter')),
  WebsiteBuilder: Loadable(() => import('./pages/admin/WebsiteBuilder')),
  AIManagementPage: Loadable(() => import('./pages/admin/AIManagementPage')),
  APISettingsPage: Loadable(() => import('./pages/admin/APISettingsPage')),
  ClientActivityMonitor: Loadable(() => import('./pages/admin/ClientActivityMonitor')),
  UserFeedback: Loadable(() => import('./pages/admin/UserFeedback')),
  ClientPortalSettings: Loadable(() => import('./pages/admin/ClientPortalSettings')),
  EnterpriseClients: Loadable(() => import('./pages/admin/EnterpriseClientsPage'))
};

// Preload admin components when user is likely to navigate there
const preloadAdminComponents = () => {
  if (typeof window !== 'undefined') {
    Object.values(adminPages).forEach(component => {
      if (component.preload) component.preload();
    });
  }
};

// Legal pages
const PrivacyPage = Loadable(() => import('./pages/PrivacyPage'));
const TermsPage = Loadable(() => import('./pages/TermsPage'));
const SecurityPage = Loadable(() => import('./pages/SecurityPage'));
const CompliancePage = Loadable(() => import('./pages/CompliancePage'));
const CookiesPage = Loadable(() => import('./pages/CookiesPage'));
const GdprPage = Loadable(() => import('./pages/GdprPage'));

// Other pages
const PressPage = Loadable(() => import('./pages/PressPage'));
const WebinarsPage = Loadable(() => import('./pages/WebinarsPage'));
const CaseStudiesPage = Loadable(() => import('./pages/CaseStudiesPage'));
const HelpCenterPage = Loadable(() => import('./pages/HelpCenterPage'));
const StatusPage = Loadable(() => import('./pages/StatusPage'));
const AIEthicsPage = Loadable(() => import('./pages/AIEthicsPage'));

// Test and example pages
const ExampleShadcnPage = Loadable(() => import('./pages/client/ExampleShadcnPage'));
const TestDashboard = Loadable(() => import('./pages/TestDashboard'));

// Tier-specific components
const SMBDashboard = Loadable(() => import('./pages/tiers/SMBDashboard'));
const ProfessionalDashboard = Loadable(() => import('./pages/tiers/ProfessionalDashboard'));
const EnterpriseDashboard = Loadable(() => import('./pages/tiers/EnterpriseDashboard'));
const TierDemoPage = Loadable(() => import('./pages/TierDemoPage'));
const TierSwitcher = Loadable(() => import('./pages/TierSwitcher'));
const DebugFreeTier = Loadable(() => import('./pages/DebugFreeTier'));
const FixFreeTier = Loadable(() => import('./pages/FixFreeTier'));
const SignupWithTier = Loadable(() => import('./pages/SignupWithTier'));
const UserDashboard = Loadable(() => import('./components/routing/UserDashboard'));
const DebugTierPage = Loadable(() => import('./pages/DebugTierPage'));
const DebugTierInfo = Loadable(() => import('./pages/DebugTierInfo'));

// Component to handle route-based preloading
const RoutePreloader = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Preload admin components if we're on a page that might lead to admin
    if (location.pathname.includes('/admin') || location.pathname === '/dashboard') {
      preloadAdminComponents();
    }
  }, [location.pathname]);
  
  return null;
};

// Component to render FloatingTeamChat with user context
const FloatingChatWrapper = () => {
  const { user, isAuthenticated } = useUser();
  const location = useLocation();
  
  // Hide team chat for Free and SMB tiers (free users and small businesses don't need team collaboration)
  const isFreeTier = location.pathname.startsWith('/free/');
  const isSMBTier = location.pathname.startsWith('/smb/');
  if (isFreeTier || isSMBTier) {
    return null;
  }
  
  // Check if we're on a tier dashboard (demo mode)
  const isDashboardPage = location.pathname.includes('/dashboard');
  const isTierPage = location.pathname.startsWith('/smb/') || 
                     location.pathname.startsWith('/professional/') || 
                     location.pathname.startsWith('/enterprise/');
  
  
  // Pages where chat bubble should NOT appear
  const excludedPaths = [
    // Public pages
    '/', '/login', '/signup', '/pricing', '/features', '/about', '/contact', 
    '/blog', '/forgot-password', '/privacy', '/terms', '/security', '/compliance',
    '/cookies', '/gdpr', '/ai-ethics', '/press', '/webinars', '/case-studies',
    '/help-center', '/status', '/careers', '/resources', '/integrations',
    '/documentation', '/demo', '/enterprise-pricing', '/enterprise-contact',
    '/contact-sales', '/checkout',
    
    // Admin pages (has own communication tools)
    '/admin'
  ];
  
  // Check if current path should be excluded
  const isExcludedPage = excludedPaths.some(path => location.pathname === path) || 
                         location.pathname.startsWith('/blog/') ||
                         location.pathname.startsWith('/admin/');
  
  // Exclude AI Assistant pages (they have their own chat interface)
  const isAIAssistantPage = location.pathname.includes('/ai-assistant');
  
  console.log('🔍 FloatingChatWrapper Checks:', {
    isExcludedPage,
    isAIAssistantPage,
    shouldShow: (isAuthenticated || isTierPage) && !isExcludedPage && !isAIAssistantPage
  });
  
  // Show on tier pages even without authentication (demo mode)
  // OR show for authenticated users on core feature pages
  if (isExcludedPage || isAIAssistantPage) {
    console.log('❌ FloatingChat NOT showing because:', {
      isExcluded: isExcludedPage,
      isAIPage: isAIAssistantPage
    });
    return null;
  }
  
  // If not authenticated but on tier page, use demo user
  if (!isAuthenticated && isTierPage) {
    console.log('✅ FloatingChat VISIBLE (Demo Mode)');
    const demoUser = {
      id: 'demo-user-' + Date.now(),
      name: 'Demo User',
      email: 'demo@plugly.ai',
      tier: location.pathname.split('/')[1] // smb, professional, or enterprise
    };
    return <FloatingTeamChat currentUser={demoUser} />;
  }
  
  // If authenticated, require user object
  if (!user) {
    console.log('❌ FloatingChat NOT showing - no user object');
    return null;
  }
  
  console.log('✅ FloatingChat SHOULD BE VISIBLE');
  
  // Ensure user has required properties for chat
  const chatUser = {
    id: user.id || user._id || 'user-' + Date.now(),
    name: user.name || user.username || 'User',
    email: user.email || 'user@example.com',
    ...user
  };
  
  return <FloatingTeamChat currentUser={chatUser} />;
};

// Inner component that uses router hooks
const AppRoutes = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const location = useLocation();

  // Handle route changes
  useEffect(() => {
    const handleStart = () => setIsLoading(true);
    const handleComplete = () => {
      setIsLoading(false);
      // Scroll to top on route change
      window.scrollTo(0, 0);
    };

    // Simulate loading for demo purposes
    const timer = setTimeout(handleComplete, 300);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <GoogleAnalytics />
      <RoutePreloader />
      {/* Global loading indicator */}
      {isLoading && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-blue-500 z-50 animate-pulse"></div>
      )}
      <ErrorBoundary>
        <Routes>
                    <Route path="/" element={<HomePage />} />
                    {/* Redirect old routes to tier-specific routing */}
                    <Route path="/dashboard" element={<DashboardRedirect />} />
                    <Route path="/client-portal" element={<Navigate to="/smb/dashboard" replace />} />
                    <Route path="/ai-assistant" element={<Navigate to="/smb/ai-assistant" replace />} />
                    <Route path="/campaigns" element={<Navigate to="/smb/campaigns" replace />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/add-ons" element={<AddOnsPage />} />
                    <Route path="/enterprise-pricing" element={<EnterprisePricingPage />} />
                    <Route path="/enterprise-contact" element={<EnterpriseContactPage />} />
                    <Route path="/contact-sales" element={<ContactSalesPage />} />
                    <Route path="/test-dashboard" element={<TestDashboard />} />
                    
                    {/* Public Pages */}
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/ai-agents" element={<AIAgentsPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    
                    {/* Resource Pages */}
                    <Route path="/demo" element={<PlatformDemo />} />
                    <Route path="/demo-workspace" element={<DemoWorkspacePage />} />
                    <Route path="/demo-full-featured" element={<FullFeaturedExample />} />
                    <Route path="/resources/ai-marketing-playbook" element={<AIMarketingPlaybook />} />
                    <Route path="/resources/templates" element={<ContentTemplates />} />
                    <Route path="/integrations" element={<IntegrationsPage />} />
                    <Route path="/documentation" element={<DocumentationPage />} />
                    <Route path="/ai-documentation" element={<AIDocumentationPage />} />
                    <Route path="/careers" element={<CareersPage />} />
                    <Route path="/press" element={<PressPage />} />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route path="/webinars" element={<WebinarsPage />} />
                    <Route path="/case-studies" element={<CaseStudiesPage />} />
                    <Route path="/help-center" element={<HelpCenterPage />} />
                    <Route path="/status" element={<StatusPage />} />
                    
                    {/* Auth Pages */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    
                    {/* Payment Pages */}
                    <Route path="/checkout" element={<CheckoutPage />} />
                    
                    {/* Legal Pages */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/compliance" element={<CompliancePage />} />
                    <Route path="/cookies" element={<CookiesPage />} />
                    <Route path="/gdpr" element={<GdprPage />} />
                    <Route path="/ai-ethics" element={<AIEthicsPage />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <AdminLayout />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }>
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<adminPages.SimplifiedDashboard />} />
                      
                      {/* User Management */}
                      <Route path="users" element={<adminPages.UserManagement />} />
                      
                      {/* Enterprise Clients */}
                      <Route path="enterprise-clients" element={<adminPages.EnterpriseClients />} />
                      
                      {/* AI Management */}
                      <Route path="ai-management" element={<adminPages.AIManagementPage />} />
                      
                      {/* API Settings */}
                      <Route path="api-settings" element={<adminPages.APISettingsPage />} />
                      
                      {/* Client Activity Monitor */}
                      <Route path="client-activity" element={<adminPages.ClientActivityMonitor />} />
                      
                      {/* Activity Log */}
                      <Route path="activity" element={<adminPages.ActivityLog />} />
                      
                      {/* Settings */}
                      <Route path="settings" element={<adminPages.ClientPortalSettings />} />
                      
                      {/* User Feedback */}
                      <Route path="feedback" element={<adminPages.UserFeedback />} />
                    </Route>
                    <Route path="/example-shadcn" element={<ExampleShadcnPage />} />
                    <Route path="/tiers" element={<TierDemoPage />} />
                    <Route path="/tier-switcher" element={<TierSwitcher />} />
                    <Route path="/debug-free" element={<DebugFreeTier />} />
                    <Route path="/fix-free-tier" element={<FixFreeTier />} />
                    
                    {/* Tier-specific routing with complete separation */}
                    <Route path="/free/*" element={<TierRouter tier="free" />} />
                    <Route path="/smb/*" element={<TierRouter tier="smb" />} />
                    <Route path="/professional/*" element={<TierRouter tier="professional" />} />
                    <Route path="/enterprise/*" element={<TierRouter tier="enterprise" />} />
                    
                    {/* Legacy routes - redirect to professional */}
                    <Route path="/medium/*" element={<Navigate to="/professional" replace />} />
                    <Route path="/premium/*" element={<Navigate to="/professional" replace />} />
                    
                    {/* DIRECT ANALYTICS ROUTES - Using original AnalyticsPage */}
                    <Route path="/smb/analytics" element={<AnalyticsPage tier="smb" />} />
                    <Route path="/professional/analytics" element={<AnalyticsPage tier="professional" />} />
                    <Route path="/enterprise/analytics" element={<AnalyticsPage tier="enterprise" />} />
                    
                    {/* Direct tier dashboard access (fallback) */}
                    <Route path="/smb" element={<SMBDashboard />} />
                    <Route path="/professional" element={<ProfessionalDashboard />} />
                    <Route path="/enterprise" element={<EnterpriseDashboard />} />
                    
                    {/* Legacy redirects */}
                    <Route path="/medium" element={<Navigate to="/professional" replace />} />
                    
                    {/* Additional redirects for old routes */}
                    <Route path="/librechat" element={<Navigate to="/smb/ai-assistant" replace />} />
                    
                    {/* New tier-aware routes */}
                    <Route path="/signup-tier" element={<SignupWithTier />} />
                    <Route path="/user-dashboard" element={<UserDashboard />} />
                    <Route path="/debug-tier" element={<DebugTierPage />} />
                    <Route path="/debug-tier-info" element={<DebugTierInfo />} />
        </Routes>
      </ErrorBoundary>
      
      {/* Floating Team Chat - Auto-hides when AI Drawer is open */}
      <FloatingChatWrapper />
    </>
  );
};

// Main App component
const App = React.memo(() => {
  // Preload critical resources on initial load
  useEffect(() => {
    if (typeof window !== 'undefined') {
      preloadCriticalResources();
    }
  }, []);

  return (
    <HelmetProvider>
      <UserProvider>
        <AddOnProvider>
          <Router>
            <AuthProvider>
              <UserTierProvider>
                <UserSkillProvider>
                  <AIDrawerProvider>
                    <AppRoutes />
                    <ToastContainer />
                  </AIDrawerProvider>
                </UserSkillProvider>
              </UserTierProvider>
            </AuthProvider>
          </Router>
        </AddOnProvider>
      </UserProvider>
    </HelmetProvider>
  );
});

export default App;
// trigger rebuild
